import * as styles from "./Fieldset.css";

import { clsx } from "clsx";
import { Children, type JSX, isValidElement } from "react";

import { Collapsible as BaseCollapsible } from "@base-ui/react/collapsible";
import { Fieldset as BaseFieldset } from "@base-ui/react/fieldset";
import {
  FieldContext,
  makeFieldContext,
  useFieldContext,
} from "@frontend/components/atoms/Field";
import { ControlContext, makeControlContext } from "@frontend/contexts";

import { FieldsetContext, makeFieldsetContext } from "./Fieldset.context";
import type { FieldsetProps } from "./Fieldset.types";
import { FieldsetLegend } from "./FieldsetLegend";

export function Fieldset({
  orientation,
  size,
  labelAlign,
  errorDisplay,
  variant = "plain",
  gap,
  width,
  collapsible = false,
  open,
  defaultOpen = true,
  onOpenChange,
  className,
  children,
  ...props
}: FieldsetProps): JSX.Element {
  const inherited = useFieldContext();
  const resolvedOrientation = orientation ?? inherited.orientation;
  const resolvedSize = size ?? inherited.size;
  const resolvedLabelAlign = labelAlign ?? inherited.labelAlign;
  const resolvedErrorDisplay = errorDisplay ?? inherited.errorDisplay;

  const childArray = Children.toArray(children);
  const legend = childArray.find(
    (child) => isValidElement(child) && child.type === FieldsetLegend,
  );

  const hasLegend = variant === "card" && Boolean(legend);

  const fieldset = (
    <BaseFieldset.Root
      data-slot="fieldset"
      className={clsx(
        styles.fieldset({
          orientation: resolvedOrientation,
          grouped: inherited.grouped,
          variant,
          gap,
          hasLegend,
          size: resolvedSize,
          width,
        }),
        className,
      )}
      {...props}
    >
      {collapsible ? (
        <>
          {legend}
          <BaseCollapsible.Panel
            data-slot="fieldset-content"
            className={styles.content}
          >
            {childArray.filter((child) => child !== legend)}
          </BaseCollapsible.Panel>
        </>
      ) : (
        children
      )}
    </BaseFieldset.Root>
  );

  return (
    <FieldContext.Provider
      value={makeFieldContext({
        orientation: resolvedOrientation,
        size: resolvedSize,
        labelAlign: resolvedLabelAlign,
        errorDisplay: resolvedErrorDisplay,
        grouped: true,
      })}
    >
      <ControlContext.Provider
        value={makeControlContext({ size: resolvedSize })}
      >
        <FieldsetContext.Provider
          value={makeFieldsetContext({ variant, collapsible })}
        >
          {collapsible ? (
            <BaseCollapsible.Root
              data-slot="fieldset-collapsible"
              className={styles.collapsibleRoot}
              open={open}
              defaultOpen={defaultOpen}
              onOpenChange={onOpenChange}
            >
              {fieldset}
            </BaseCollapsible.Root>
          ) : (
            fieldset
          )}
        </FieldsetContext.Provider>
      </ControlContext.Provider>
    </FieldContext.Provider>
  );
}
